
import { useState } from "react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Package } from "lucide-react";

interface ProductImageSlideshowProps {
  images: string[];
  productTitle: string;
}

const ProductImageSlideshow = ({ images, productTitle }: ProductImageSlideshowProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Filter out any invalid/empty image URLs
  const validImages = images.filter(img => img && img.trim() !== '');

  const nextImage = () => {
    if (validImages.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % validImages.length);
    }
  };

  const prevImage = () => {
    if (validImages.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + validImages.length) % validImages.length);
    }
  };

  if (validImages.length === 0) {
    return (
      <AspectRatio ratio={1} className="bg-gray-100 flex items-center justify-center">
        <Package className="h-12 w-12 text-gray-400" />
      </AspectRatio>
    );
  }

  // For single images, just show the image without slideshow controls
  if (validImages.length === 1) {
    return (
      <AspectRatio ratio={1} className="bg-gray-100">
        <img
          src={validImages[0]}
          alt={productTitle}
          className="object-cover w-full h-full"
          onError={(e) => {
            console.log('Image failed to load:', validImages[0]);
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const parent = target.parentElement;
            if (parent) {
              parent.innerHTML = '<div class="flex items-center justify-center h-full w-full"><div class="h-12 w-12 text-gray-400"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.64 16.2a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg></div></div>';
            }
          }}
        />
      </AspectRatio>
    );
  }

  return (
    <div className="relative">
      <AspectRatio ratio={1} className="bg-gray-100">
        <img
          src={validImages[currentImageIndex]}
          alt={`${productTitle} - Image ${currentImageIndex + 1}`}
          className="object-cover w-full h-full"
          onError={(e) => {
            console.log('Image failed to load:', validImages[currentImageIndex]);
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const parent = target.parentElement;
            if (parent) {
              parent.innerHTML = '<div class="flex items-center justify-center h-full w-full"><div class="h-12 w-12 text-gray-400"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.64 16.2a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg></div></div>';
            }
          }}
        />
      </AspectRatio>

      {/* Navigation Buttons - only show for multiple images */}
      <Button
        variant="secondary"
        size="sm"
        className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0 bg-white/80 hover:bg-white/90"
        onClick={prevImage}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      
      <Button
        variant="secondary"
        size="sm"
        className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0 bg-white/80 hover:bg-white/90"
        onClick={nextImage}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>

      {/* Image Indicators */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
        {validImages.map((_, index) => (
          <button
            key={index}
            className={`h-2 w-2 rounded-full transition-colors ${
              index === currentImageIndex ? 'bg-white' : 'bg-white/50'
            }`}
            onClick={() => setCurrentImageIndex(index)}
          />
        ))}
      </div>

      {/* Image Counter */}
      <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
        {currentImageIndex + 1} / {validImages.length}
      </div>
    </div>
  );
};

export default ProductImageSlideshow;
